import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { STALE } from '@/lib/query-cache.js';
import { LIVE_VIEW } from './useIssues.js';
import type { JiraBoard, JiraSprint, JiraIssueSummary } from '@shared/types.js';

export function useBoards() {
  return useQuery({
    queryKey: ['boards'],
    queryFn: () => api.get<JiraBoard[]>('/api/boards').then((r) => r.data),
    staleTime: STALE.METADATA,  // 24 hours — board list rarely changes
  });
}

interface BoardConfig {
  columns: Array<{ name: string; statusIds: string[] }>;
}

export function useBoardConfig(boardId: number | null) {
  return useQuery({
    queryKey: ['board', boardId, 'config'],
    queryFn: () => api.get<BoardConfig>(`/api/boards/${boardId}/config`).then((r) => r.data),
    enabled: boardId !== null,
    staleTime: STALE.METADATA,  // 24 hours — columns rarely change
  });
}

interface BoardIssue extends JiraIssueSummary {
  statusId: string | null;
}

/**
 * A board re-reads like every live view, except while a card move is being written: a re-read
 * then (on opening the board, returning to the tab, reconnecting or on the timer) would put the
 * card back where it was until the move settles and refreshes the board.
 */
function boardRefresh(movePending: boolean) {
  return movePending
    ? { ...LIVE_VIEW, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchInterval: false } as const
    : LIVE_VIEW;
}

export function useBoardIssues(boardId: number | null, movePending = false) {
  return useQuery({
    queryKey: ['board', boardId, 'issues'],
    queryFn: async () => {
      const allIssues: BoardIssue[] = [];
      let startAt = 0;
      const pageSize = 100;
      let total = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await api.get<BoardIssue[]>(`/api/boards/${boardId}/issues?startAt=${startAt}&maxResults=${pageSize}`);
        const meta = res.meta as Record<string, unknown> | undefined;
        total = (meta?.total as number) ?? 0;
        allIssues.push(...res.data);

        if (allIssues.length >= total || res.data.length < pageSize) break;
        startAt += pageSize;
      }

      return { issues: allIssues, total };
    },
    enabled: boardId !== null,
    ...boardRefresh(movePending),
  });
}

interface KanbanBacklogData {
  boardIssueIds: number[];
  issues: Array<{
    id: number;
    key: string;
    summary: string;
    done: boolean;
    typeName: string;
    priorityName: string;
    assigneeName: string | null;
  }>;
}

/**
 * Uses the Greenhopper API to get the real board/backlog divider.
 * Returns all issues + boardIssueIds to distinguish board vs backlog.
 */
export function useKanbanBacklog(boardId: number | null, movePending = false) {
  return useQuery({
    queryKey: ['board', boardId, 'kanban-backlog'],
    queryFn: async () => {
      const res = await api.get<KanbanBacklogData>(`/api/boards/${boardId}/kanban-backlog`);
      return res.data;
    },
    enabled: boardId !== null,
    ...boardRefresh(movePending),
  });
}

export function useBoard(boardId: number | null) {
  const { data: boards } = useBoards();
  const board = boards?.find((b) => b.id === boardId) ?? null;
  return board;
}

export function useBoardSprints(boardId: number | null) {
  return useQuery({
    queryKey: ['board', boardId, 'sprints'],
    queryFn: () => api.get<JiraSprint[]>(`/api/boards/${boardId}/sprints`).then((r) => r.data),
    enabled: boardId !== null,
    staleTime: STALE.MODERATE,  // 10 min
  });
}

export function useSprintIssues(boardId: number | null, sprintId: number | null) {
  return useQuery({
    queryKey: ['sprint', sprintId, 'issues'],
    queryFn: () => api.get<JiraIssueSummary[]>(`/api/boards/${boardId}/sprints/${sprintId}/issues`).then((r) => r.data),
    enabled: boardId !== null && sprintId !== null,
    ...LIVE_VIEW,
  });
}

export function useTransitionToBoard(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transitionId: string) =>
      api.post(`/api/issues/${issueKey}/transitions`, { transitionId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['board'] });
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
    },
  });
}
